/* Lays out and drives the paw trail.

   The snippet supplies only a route: waypoints in percent of the host section.
   This builds the walk from it in two stages.

   Layout — the waypoints are smoothed into a Catmull-Rom curve, sampled densely,
   and measured, so prints can be spaced evenly along the *curve* rather than
   evenly in x. Each print is rotated to the tangent at its position and stepped
   to alternating sides of it, with the motif mirrored for the opposite foot.
   Rotation therefore always follows the route; reshaping the route needs no
   angle bookkeeping. Positions are in px, so they are recomputed on resize.

   Reveal — as the host crosses the viewport we compute a 0-100 progress value
   and switch on every print the walk has reached. Scrolling back up switches
   them off again, so the trail scrubs with the page rather than firing once and
   being missed.

   Reads are batched into a rAF callback so scrolling never forces sync layout. */
(() => {
  const trails = [...document.querySelectorAll('[data-paw-trail]')];
  if (!trails.length) return;

  const GAIT = 15;    // px each print sits off the centre line
  const SAMPLES = 600;

  /* The four prints from the palette board. They are hand-drawn, so each leans a
     little off vertical; `base` is that lean in degrees clockwise, removed so
     every motif points along the route the same way. Cycling them keeps a walk
     from repeating the same print twice running. */
  const MOTIFS = [
    { id: 'motif-paw-1', box: '0 0 580 480', base: -17 },
    { id: 'motif-paw-2', box: '0 0 560 480', base: -14 },
    { id: 'motif-paw-3', box: '0 0 460 400', base: -4 },
    { id: 'motif-paw-4', box: '0 0 340 300', base: -13 }
  ];

  const parseRoute = (s) => (s || '').trim().split(/\s+/).map((pair) => {
    const [x, y] = pair.split(',').map(Number);
    return { x, y };
  }).filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));

  /* Centripetal Catmull-Rom (alpha = 0.5) through the waypoints, in px.

     The uniform form this replaced cusped wherever consecutive waypoints were
     unevenly spaced, which read as hard pointed corners in the walk. The
     centripetal parameterisation is the standard fix: it cannot cusp or form a
     loop, whatever the spacing, so a route can be drawn by feel without having
     to keep its waypoints evenly apart. */
  const sample = (pts, w, h) => {
    const p = pts.map((q) => ({ x: (q.x / 100) * w, y: (q.y / 100) * h }));
    // Phantom ends, reflected rather than duplicated: a duplicate makes a
    // zero-length segment, which this parameterisation divides by.
    const first = { x: 2 * p[0].x - p[1].x, y: 2 * p[0].y - p[1].y };
    const lastI = p.length - 1;
    const last = { x: 2 * p[lastI].x - p[lastI - 1].x, y: 2 * p[lastI].y - p[lastI - 1].y };
    const c = [first, ...p, last];

    const knot = (t, a, b) => t + Math.pow(Math.hypot(b.x - a.x, b.y - a.y), 0.5);
    const out = [];
    for (let i = 0; i < c.length - 3; i++) {
      const [p0, p1, p2, p3] = [c[i], c[i + 1], c[i + 2], c[i + 3]];
      const t0 = 0, t1 = knot(t0, p0, p1), t2 = knot(t1, p1, p2), t3 = knot(t2, p2, p3);
      const n = Math.ceil(SAMPLES / (c.length - 3));
      for (let j = 0; j < n; j++) {
        const t = t1 + (t2 - t1) * (j / n);
        const lerp = (a, b, ta, tb) => {
          const k = (tb - ta) || 1;
          return { x: ((tb - t) * a.x + (t - ta) * b.x) / k, y: ((tb - t) * a.y + (t - ta) * b.y) / k };
        };
        const a1 = lerp(p0, p1, t0, t1), a2 = lerp(p1, p2, t1, t2), a3 = lerp(p2, p3, t2, t3);
        const b1 = lerp(a1, a2, t0, t2), b2 = lerp(a2, a3, t1, t3);
        out.push(lerp(b1, b2, t1, t2));
      }
    }
    out.push(p[lastI]);
    // Cumulative arc length, so we can walk the curve at a constant pace.
    let run = 0;
    out[0].d = 0;
    for (let i = 1; i < out.length; i++) {
      run += Math.hypot(out[i].x - out[i - 1].x, out[i].y - out[i - 1].y);
      out[i].d = run;
    }
    return { pts: out, length: run };
  };

  /* Deterministic per-print jitter. A dog does not place its feet on a ruler, and
     a perfectly even alternation reads as a stamped pattern rather than a walk.
     Seeded off the print index so the walk is identical on every load. */
  const wobble = (n) => {
    const x = Math.sin(n * 12.9898) * 43758.5453;
    return x - Math.floor(x) - 0.5;
  };

  const at = (curve, frac) => {
    const target = curve.length * frac;
    const { pts } = curve;
    let i = 1;
    while (i < pts.length - 1 && pts[i].d < target) i++;
    const a = pts[i - 1], b = pts[i];
    const span = b.d - a.d || 1;
    const t = (target - a.d) / span;
    return {
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
      // Tangent direction of travel at this point.
      angle: Math.atan2(b.y - a.y, b.x - a.x)
    };
  };

  const build = (trail) => {
    const route = parseRoute(trail.dataset.route);
    if (route.length < 2) return [];
    const host = trail.parentElement;
    const w = host.offsetWidth, h = host.offsetHeight;
    if (!w || !h) return [];
    const curve = sample(route, w, h);
    const steps = parseInt(trail.dataset.steps, 10) || 14;

    trail.textContent = '';
    const made = [];

    for (let i = 0; i < steps; i++) {
      // Inset from both ends so the walk enters and leaves cleanly, then nudged
      // off the beat so the stride is not metronomic.
      const frac = Math.max(0, Math.min(1, (i + 0.5) / steps + wobble(i + 71) * 0.5 / steps));
      const { x, y, angle } = at(curve, frac);
      const side = i % 2 ? 1 : -1;
      // Step off the centre line, perpendicular to travel, by a varying amount.
      const reach = GAIT * (1 + wobble(i + 13) * 0.55);
      const ox = Math.cos(angle + Math.PI / 2) * reach * side;
      const oy = Math.sin(angle + Math.PI / 2) * reach * side;
      const motif = MOTIFS[i % MOTIFS.length];
      const el = document.createElement('span');
      el.className = 'paw-trail__print';
      // The motifs are drawn toes-up, so a quarter turn brings that in line with
      // the tangent. The lean correction is mirrored along with the print, hence
      // multiplying it by the same side.
      const tilt = (angle * 180 / Math.PI) + 90 - motif.base * side + wobble(i + 29) * 16;
      el.style.cssText =
        `--x:${(x + ox).toFixed(1)}px;--y:${(y + oy).toFixed(1)}px;` +
        `--tilt:${tilt.toFixed(1)}deg;--flip:${side};` +
        `--at:${(frac * 100).toFixed(1)}`;
      el.innerHTML = `<svg viewBox="${motif.box}" focusable="false"><use href="#${motif.id}"></use></svg>`;
      trail.appendChild(el);
      made.push({ el, at: frac * 100 });
    }

    return made;
  };

  let items = [];
  const layout = () => {
    items = trails.map((trail) => ({ host: trail.parentElement, prints: build(trail) }));
  };

  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let queued = false;
  const update = () => {
    queued = false;
    const vh = window.innerHeight;
    for (const { host, prints } of items) {
      if (still) { prints.forEach(({ el }) => el.classList.add('is-on')); continue; }
      const r = host.getBoundingClientRect();
      // 0 when the section's top reaches the bottom of the viewport,
      // 100 once its bottom has risen past the top.
      const progress = Math.max(0, Math.min(100, ((vh - r.top) / (r.height + vh)) * 100));
      // Stretch the middle of that range so the walk completes while the section
      // is still on screen. Tall sections otherwise leave it half-finished by the
      // time they scroll away.
      const eased = Math.max(0, Math.min(100, (progress - 12) * (100 / 46)));
      for (const { el, at: a } of prints) el.classList.toggle('is-on', eased >= a);
    }
  };

  const onScroll = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  };

  let resizeTimer;
  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => { layout(); update(); }, 150);
  }, { passive: true });

  layout();
  update();
  // Images settling change section heights; re-place once everything has loaded.
  addEventListener('load', () => { layout(); update(); });
})();
